import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Col, Descriptions, Input, Radio, Row, Space, Switch, Typography, Upload, message } from 'antd';
import { FileSearchOutlined, FolderOpenOutlined, UploadOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';

import { useAuth } from '../../components/common/security/AuthContext';
import { dccOcr, type DccOcrResponse } from '../../services/dccApi';
import SelectDCCFileModal from '../../components/modals/SelectDCCFileModal';
import Button from '../../components/common/Button';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

type InputMode = 'upload' | 'server';

const CorOcr: React.FC = () => {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [inputMode, setInputMode] = useState<InputMode>('upload');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [serverFilePath, setServerFilePath] = useState('');
  const [forceOcr, setForceOcr] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<DccOcrResponse | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [previewType, setPreviewType] = useState<'image' | 'pdf' | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [pendingOpenPicker, setPendingOpenPicker] = useState(false);
  const [isDccModalOpen, setIsDccModalOpen] = useState(false);

  const canSubmit = useMemo(() => {
    if (inputMode === 'upload') {
      return !!selectedFile;
    }
    return !!serverFilePath.trim();
  }, [inputMode, selectedFile, serverFilePath]);

  const applySelectedFile = (f: File) => {
    setSelectedFile(f);
    setResult(null);
    try {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch {}
      }
      const url = URL.createObjectURL(f);
      setPreviewUrl(url);
      if (f.type === 'application/pdf' || /\.pdf$/i.test(f.name)) {
        setPreviewType('pdf');
      } else if (f.type.startsWith('image/')) {
        setPreviewType('image');
      } else {
        setPreviewType(null);
      }
    } catch {
      setPreviewUrl(null);
      setPreviewType(null);
    }
  };

  const handleRunOcr = async () => {
    if (!token) {
      message.error(t('corOcr.msgNoSession'));
      return;
    }

    if (inputMode === 'upload' && !selectedFile) {
      message.warning(t('corOcr.msgSelectFile'));
      return;
    }

    if (inputMode === 'server' && !serverFilePath.trim()) {
      message.warning(t('corOcr.msgEnterPath'));
      return;
    }

    setLoading(true);
    setResult(null);
    try {
      const response = await dccOcr(
        inputMode === 'upload'
          ? { file: selectedFile || undefined, forceOcr }
          : { filePath: serverFilePath.trim(), forceOcr },
        token
      );
      setResult(response);
      message.success(t('corOcr.msgDone'));
    } catch (error: any) {
      message.error(error?.message || t('corOcr.msgFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Revoke blob URL on unmount or when URL changes
  useEffect(() => {
    return () => {
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch {}
      }
    };
  }, [previewUrl]);

  // Reset preview when switching to server mode
  useEffect(() => {
    if (inputMode === 'server') {
      setSelectedFile(null);
      if (previewUrl) {
        try { URL.revokeObjectURL(previewUrl); } catch {}
      }
      setPreviewUrl(null);
      setPreviewType(null);
    }
  }, [inputMode]);

  // If user selected 'Yerel Dosya', open file picker after render
  useEffect(() => {
    if (inputMode === 'upload' && pendingOpenPicker) {
      // defer to ensure input is mounted
      setTimeout(() => fileInputRef.current?.click(), 0);
      setPendingOpenPicker(false);
    }
  }, [inputMode, pendingOpenPicker]);

  return (
    <div style={{ padding: 24 }}>
      <Space direction="vertical" size="large" style={{ width: '100%' }}>
        <div>
          <Title level={2} style={{ marginBottom: 8 }}>
            {t('corOcr.pageTitle')}
          </Title>
          <Paragraph style={{ marginBottom: 0 }}>{t('corOcr.pageDescription')}</Paragraph>
        </div>

        <Alert
          type="info"
          showIcon
          message={t('corOcr.engineTitle')}
          description={t('corOcr.engineDescription')}
        />

        <Row gutter={[16, 16]}>
          <Col xs={24} lg={10}>
            <Card title={t('corOcr.sourceCard')}>
              <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                <Radio.Group
                  value={inputMode}
                  onChange={(e) => {
                    const v = e.target.value as InputMode;
                    setInputMode(v);
                    if (v === 'upload') setPendingOpenPicker(true);
                    if (v === 'server') setIsDccModalOpen(true);
                  }}
                  optionType="button"
                  buttonStyle="solid"
                >
                  <Radio.Button
                    value="upload"
                    onClick={() => {
                      // If already in upload mode, clicking should open picker
                      if (inputMode === 'upload') {
                        fileInputRef.current?.click();
                      }
                    }}
                  >
                    {t('corOcr.localFile')}
                  </Radio.Button>
                  <Radio.Button
                    value="server"
                    onClick={() => {
                      // If already in server mode, clicking should reopen the DCC modal
                      if (inputMode === 'server') {
                        setIsDccModalOpen(true);
                      }
                    }}
                  >
                    {t('corOcr.serverPath')}
                  </Radio.Button>
                </Radio.Group>

                {/* Hidden input used for opening native file picker */}
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".pdf,.png,.jpg,.jpeg,.bmp,.tif,.tiff,.webp"
                  style={{ display: 'none' }}
                  onChange={(e) => {
                    const f = e.target.files && e.target.files[0];
                    if (f) {
                      applySelectedFile(f);
                      try { e.target.value = ''; } catch {}
                    }
                  }}
                />

                {inputMode === 'upload' ? (
                  <>
                  <Upload.Dragger
                    accept=".pdf,.png,.jpg,.jpeg,.bmp,.tif,.tiff,.webp"
                    showUploadList={false}
                    maxCount={1}
                    beforeUpload={(file) => {
                      const f = file as File;
                      applySelectedFile(f);
                      return false;
                    }}
                    onChange={(info) => {
                      const f = (info?.file?.originFileObj || info?.file) as File | undefined;
                      if (f) applySelectedFile(f);
                    }}
                    disabled={loading}
                  >
                    {previewUrl ? (
                      <div style={{ width: '100%' }}>
                        {previewType === 'image' && (
                          <img
                            src={previewUrl}
                            alt="preview"
                            style={{ maxHeight: 240, width: 'auto', maxWidth: '100%', display: 'block', margin: '0 auto' }}
                          />
                        )}
                        {previewType === 'pdf' && (
                          <iframe
                            src={previewUrl}
                            title="pdf-preview"
                            style={{ width: '100%', height: 240, border: 0 }}
                          />
                        )}
                        {!previewType && (
                          <>
                            <p className="ant-upload-drag-icon">
                              <UploadOutlined />
                            </p>
                            <p className="ant-upload-text">{t('corOcr.dropText')}</p>
                            <p className="ant-upload-hint">{t('corOcr.dropHint')}</p>
                          </>
                        )}
                      </div>
                    ) : (
                      <>
                        <p className="ant-upload-drag-icon">
                          <UploadOutlined />
                        </p>
                        <p className="ant-upload-text">{t('corOcr.dropText')}</p>
                        <p className="ant-upload-hint">{t('corOcr.dropHint')}</p>
                      </>
                    )}
                  </Upload.Dragger>
                  </>
                ) : (
                  <>
                    <Input
                      value={serverFilePath}
                      onChange={(e) => setServerFilePath(e.target.value)}
                      placeholder={t('corOcr.pathPlaceholder')}
                      disabled={loading}
                    />
                    <Button
                      icon={<FolderOpenOutlined />}
                      onClick={() => setIsDccModalOpen(true)}
                      disabled={loading}
                    >
                      {t('corOcr.selectFromDcc')}
                    </Button>
                    {serverFilePath && (
                      <Text type="secondary">
                        {t('corOcr.selectedServerPath', { path: serverFilePath })}
                      </Text>
                    )}
                  </>
                )}

                {inputMode === 'upload' && selectedFile && (
                  <Text type="secondary">
                    {t('corOcr.selectedFile', { name: selectedFile.name })}
                  </Text>
                )}

                <Space align="center">
                  <Switch checked={forceOcr} onChange={setForceOcr} disabled={loading} />
                  <Text>{t('corOcr.forceOcrLabel')}</Text>
                </Space>

                <Button
                  type="primary"
                  icon={<FileSearchOutlined />}
                  onClick={handleRunOcr}
                  loading={loading}
                  disabled={!canSubmit}
                >
                  {t('corOcr.extract')}
                </Button>
              </Space>
            </Card>
          </Col>

          <Col xs={24} lg={14}>
            <Card title={t('corOcr.resultCard')}>
              {result ? (
                <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                  <Descriptions size="small" bordered column={2}>
                    <Descriptions.Item label={t('corOcr.engine')}>{result.engine || '-'}</Descriptions.Item>
                    <Descriptions.Item label={t('corOcr.source')}>{result.source_type || '-'}</Descriptions.Item>
                    <Descriptions.Item label={t('corOcr.pages')}>{result.page_count ?? 0}</Descriptions.Item>
                    <Descriptions.Item label={t('corOcr.chars')}>{result.text_length ?? 0}</Descriptions.Item>
                  </Descriptions>

                  <TextArea
                    value={result.text || ''}
                    readOnly
                    autoSize={{ minRows: 14, maxRows: 24 }}
                    placeholder={t('corOcr.resultPlaceholder')}
                  />
                </Space>
              ) : (
                <Text type="secondary">{t('corOcr.noResult')}</Text>
              )}
            </Card>
          </Col>
        </Row>
      </Space>

      <SelectDCCFileModal
        open={isDccModalOpen}
        initialPath=""
        mode="single"
        onCancel={() => setIsDccModalOpen(false)}
        onSelect={({ fullPath }) => {
          setServerFilePath(fullPath);
          setResult(null);
          setIsDccModalOpen(false);
        }}
      />
    </div>
  );
};

export default CorOcr;
